import { LitElement, html, css } from "lit";
import { customElement, property } from "lit/decorators.js";

/**
 * bn-spinner is the loading indicator: a celeste-tinted ring drawn from the DS
 * tokens, so it follows light and dark without a per-component branch. Set
 * `size` in px (default 20) and optionally a `label` for an accessible loading
 * row. Set `block` to centre it in a panel-sized loading state.
 *
 * Properties:
 * - `size` — ring diameter in px; the stroke width is derived from it.
 * - `label` — visible text next to the ring; also the accessible name. When
 *   empty the ring is still named "Loading" so it is never anonymous.
 * - `block` — centre the spinner in its container with generous padding.
 *
 *   <bn-spinner></bn-spinner>
 *   <bn-spinner block size="28" label="Loading instances"></bn-spinner>
 */
@customElement("bn-spinner")
export class BnSpinner extends LitElement {
  /** Ring diameter in px. */
  @property({ type: Number }) size = 20;
  /** Visible label rendered beside the ring; also the accessible name. */
  @property() label = "";
  /** Centre the spinner in its container (full-panel loading state). */
  @property({ type: Boolean, reflect: true }) block = false;

  static override styles = css`
    :host {
      display: inline-flex;
      align-items: center;
      gap: var(--bino-space-sm);
      color: var(--bino-text-secondary);
      font-family: var(--bino-font-sans);
      font-size: var(--bino-font-size-sm);
    }
    :host([block]) {
      display: flex;
      justify-content: center;
      padding: var(--bino-space-xl);
    }
    .ring {
      flex-shrink: 0;
      border-radius: 50%;
      border-style: solid;
      border-color: var(--bino-border-light);
      border-top-color: var(--bino-primary);
      animation: spin 0.7s linear infinite;
    }
    @keyframes spin {
      to {
        transform: rotate(360deg);
      }
    }
    /* A stopped spinner conveys nothing, so reduced motion slows the ring
       rather than freezing it. */
    @media (prefers-reduced-motion: reduce) {
      .ring {
        animation-duration: 1.6s;
      }
    }
  `;

  override render() {
    const px = `${this.size}px`;
    const border = `${Math.max(2, Math.round(this.size / 10))}px`;
    return html`
      <span
        class="ring"
        role="progressbar"
        aria-label=${this.label || "Loading"}
        style="width:${px};height:${px};border-width:${border}"
      ></span>
      ${this.label ? html`<span>${this.label}</span>` : ""}
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "bn-spinner": BnSpinner;
  }
}
